import React, { useCallback, useRef, useState, useMemo } from 'react';
import TableHeader from '../../../BaseComponents/TableHeader';
import TableSkeleton from '../../../BaseComponents/TableSkeleton';
import './BiomarkersTable.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import BiomarkersTableRow from './BiomarkersTableRow';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
import {
  setBiomarkersList,
  setBiomarkerToEdit,
} from '../../../../store/appSlice';
import { changeDeleteBiomarkerModalVisibility } from '../../../../store/appSlice';
import { deleteBiomarker, getBiomarkersList, updateBiomarkerStatus } from '../../../../api/biomarkers';
import { toast } from 'react-toastify';

import { ListBiomarkerResponseElement } from '../../../../types/apiGeneratedTypes';

const headerColumns = [
  { title: 'Biomarker name', key: 'name', minWidth: '100px', flex: 0, sortable: true, apiKey: "name" },
  { title: 'tag', key: 'name', minWidth: '100px', flex: 0, sortable: false, apiKey: "" },
  { title: 'Body Systems', key: 'bodySystems', minWidth: '160px', flex: 0, sortable: true, apiKey: "body_system" },
  { title: 'Blood Panels', key: 'bloodPanels', minWidth: '120px', flex: 0, sortable: false, apiKey: "blood_panels" },
  { title: 'Summary', key: 'summary', minWidth: '150px', flex: 0, sortable: false, apiKey: "summary" },
  { title: 'Description', key: 'description', minWidth: '200px', flex: 1 },
  { title: 'Status', key: 'status', minWidth: '120px', flex: 0, sortable: false, apiKey: "" },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 },
];

interface BiomarkersTableProps {
  summaryFilter?: any;
}

const BiomarkersTable: React.FC<BiomarkersTableProps> = ({ summaryFilter }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [sortingLoading, setSortingLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { biomarkersList, isDeleteBiomarkerModalOpened, biomarkerToEdit } =
    useSelector((state: RootState) => state.app);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortBioMarkers, setSortBioMarkers] = useState<any[]>([]);
  const [togglingBiomarkerId, setTogglingBiomarkerId] = useState<number | null>(null);

  const handleCloseDeleteModalClick = useCallback(() => {
    dispatch(changeDeleteBiomarkerModalVisibility(false));
    setErrorMessage(null);
  }, [dispatch]);

  const handleDelete = useCallback(async () => {
    if (!biomarkerToEdit) return;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      await deleteBiomarker(biomarkerToEdit.id);
      dispatch(
        setBiomarkersList(
          biomarkersList.filter(
            (biomarker) => biomarker.id !== biomarkerToEdit.id
          )
        )
      );
      dispatch(setBiomarkerToEdit(null));
      dispatch(changeDeleteBiomarkerModalVisibility(false));
      toast.success('Biomarker deleted successfully');
    } catch (err: any) {
      setErrorMessage(err.message);
      toast.error(err.message);
      dispatch(changeDeleteBiomarkerModalVisibility(false));
    } finally {
      setIsLoading(false);
    }
  }, [biomarkerToEdit, biomarkersList, dispatch]);

  const handleToggleStatus = useCallback(
    async (biomarker: ListBiomarkerResponseElement) => {
      const isCurrentlyActive = biomarker.is_active !== false;

      try {
        setTogglingBiomarkerId(biomarker.id);
        await updateBiomarkerStatus({
          id: biomarker.id,
          is_active: !isCurrentlyActive,
        });

        const updatedList = biomarkersList.map((item) =>
          item.id === biomarker.id
            ? { ...item, is_active: !isCurrentlyActive }
            : item
        );
        dispatch(setBiomarkersList(updatedList));
        setSortBioMarkers((prev) =>
          prev.map((item) =>
            item.id === biomarker.id
              ? { ...item, is_active: !isCurrentlyActive }
              : item
          )
        );

        toast.success(
          isCurrentlyActive
            ? 'Biomarker disabled successfully'
            : 'Biomarker enabled successfully'
        );
      } catch (err: any) {
        toast.error(err.message || 'Failed to update biomarker status');
      } finally {
        setTogglingBiomarkerId(null);
      }
    },
    [biomarkersList, dispatch]
  );

  const onSort = async (columnKey: string, direction: 'asc' | 'desc' ) => {
    try {
      // Set sorting loading state immediately
      setSortingLoading(true);
      
      const biomarkersList = await getBiomarkersList("", columnKey, direction, true);
      setSortBioMarkers(biomarkersList.biomarkers);
    } catch (err) {
      console.error('Error sorting biomarkers:', err);
      toast.error('Failed to sort biomarkers. Please try again.');
    } finally {
      setSortingLoading(false);
    }
  };

  // Filter biomarkers based on summaryFilter
  const filteredBiomarkers = useMemo(() => {
    if (!summaryFilter || summaryFilter.key === 'all') {
      return biomarkersList;
    }

    return biomarkersList.filter((biomarker) => {
      switch (summaryFilter.key) {
        case 'age_dependent':
          return biomarker.age_dependent === true;
        case 'lifestyle_changes':
          return biomarker.lifestyle_changes && biomarker.lifestyle_changes.trim() !== '';
        case 'follow_up':
          return biomarker.follow_up && biomarker.follow_up.trim() !== '';
        case 'contraindications':
          return biomarker.contraindications && biomarker.contraindications.trim() !== '';
        case 'measurement_unit':
          return biomarker.measurement_unit && biomarker.measurement_unit.trim() !== '';
        default:
          return true;
      }
    });
  }, [biomarkersList, summaryFilter]);

  const displayBioMarkers = sortBioMarkers.length > 0 ? sortBioMarkers : filteredBiomarkers;
  
  return (
    <div className='scroll-container'>
      <div className='table-container biomarkers-table'>
        <TableHeader headerColumns={headerColumns} gap='80px' onSort={onSort}/>
        <div className='table-body' ref={scrollContainerRef}>
          {sortingLoading ? (
            <TableSkeleton rows={5} columns={5} />
          ) : displayBioMarkers?.length > 0 ? (
            displayBioMarkers.map((biomarker) => (
              <BiomarkersTableRow
                biomarkerData={biomarker}
                key={biomarker.id}
                onToggleStatus={handleToggleStatus}
                isTogglingStatus={togglingBiomarkerId === biomarker.id}
              />
            ))
          ) : (
            <div className='no-results-message'>
              {summaryFilter && summaryFilter.key !== 'all' 
                ? `No biomarkers found matching "${summaryFilter.value}" filter.`
                : 'No biomarkers found.'
              }
            </div>
          )}
        </div>
      </div>
      {isDeleteBiomarkerModalOpened && (
        <ConfirmModal
          isOpen={isDeleteBiomarkerModalOpened}
          onClose={handleCloseDeleteModalClick}
          onConfirmClick={handleDelete}
          title='Delete this biomarker?'
          subtitle={errorMessage || 'This action will permanently remove the biomarker. Once deleted, it cannot be restored.'}
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default BiomarkersTable;
